import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { createSearchParams, useNavigate } from 'react-router-dom';

import { FormCheckbox, Loader, RadioGroup, Skeleton, useBalanceContext } from 'components';
import { useFetch, useUser } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';
import { setTravelPurchase } from 'store';

import { cardIcons } from 'assets/img';

const TravelBalancePaymentMethod = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentUser } = useUser();

  const { call: createPaymentCall, loading: createPaymentLoading } = useFetch({ withSetState: false });

  const { travelOrder, orderLoading } = useBalanceContext();
  const searchParams = createSearchParams({ orderId: `${travelOrder?.id}` });

  const formBalance = useForm({
    mode: 'onChange',
    defaultValues: {
      balancePaymentMethod: '',
      balanceSalesTerms: false,
      balanceSpecificSalesTerms: false
    }
  });

  const { handleSubmit, formState } = formBalance;

  const paymentMethods = useMemo(() => {
    return [
      {
        name: 'validation.balancePaymentMethodChoice',
        value: 'card',
        uuid: 'payment-method-radio',
        content: () => (
          <div className="flex items-center">
            <span className="uppercase mr-2">{t('purchase:payment:paymentMethods.card')}</span>

            <div className="w-18 h-8">
              <img src={cardIcons} alt="" width="71" height="34" className="w-full h-full object-contain" />
            </div>
          </div>
        )
      }
    ];
  }, []);

  const getCallBackUrl = (type) => {
    const baseUrl = window.location.origin;
    const path =
      type === 'confirmation'
        ? routes.travelBalance.children.confirmation.path
        : routes.travelBalance.children.validation.path;

    const additionalParams = type === 'confirmation' ? '' : '&error=paymentFailed';

    return `${baseUrl}${path}?&orderId=${travelOrder.id}${additionalParams}`;
  };

  const createPayment = async () => {
    const doneCallback = getCallBackUrl('confirmation');
    const errorCallback = getCallBackUrl('validation');
    if (!currentUser?.serialNumber || !travelOrder?.id) return;

    const { data } =
      (await createPaymentCall({
        service: 'checkout',
        method: 'post',
        path: `/${currentUser.serialNumber}/payment/${travelOrder.id}/create/card/balance`,
        data: {
          doneCallback,
          errorCallback
        }
      })) || {};
    if (!isEmpty(data?.body)) {
      if (data.body?.type === 'INTEGRATED_FORM') {
        dispatch(setTravelPurchase({ key: 'paymentHead', value: data.body.content.head }));
        dispatch(setTravelPurchase({ key: 'paymentBody', value: data.body.content.body }));
        navigate(`${routes.travelBalance.children.payment.path}?${searchParams.toString()}`, { replace: true });
      } else if (data.body?.type === 'REDIRECTION') {
        window.location = data.body.content.url;
      }
    }
  };

  return (
    <div className="mt-8">
      <h2 className="mb-6 underlined-blue after:hidden h3 lg:after:block lg:h2">{t('purchase:payment.title')}</h2>
      {orderLoading ? (
        <>
          <Skeleton classNames="w-full h-16 mb-3 " />
          <Skeleton classNames="w-full h-8 mb-3 " />
          <Skeleton classNames="w-full h-8 mb-3 " />
        </>
      ) : (
        <>
          <FormProvider {...formBalance}>
            <form onSubmit={handleSubmit(createPayment)} className="flex flex-col gap-4 mb-4 travel-payment-step">
              {!isEmpty(travelOrder) && (
                <RadioGroup
                  radioOptions={paymentMethods}
                  labelHidden
                  className="radio-container card-radio"
                  name="balancePaymentMethod"
                  rules={{ required: true }}
                />
              )}

              <FormCheckbox
                name="balanceSalesTerms"
                labelI18nKey="purchase:payment.acceptSalesTerms"
                labelClassName="flex flex-wrap"
                link={{
                  url: `${routes.termsOfSales.path}`,
                  target: '_blank',
                  className: 'underline underline-offset-4',
                  i18nValues: { appName: appService.brand() }
                }}
                rules={{ required: true }}
              />
              <FormCheckbox
                name="balanceSpecificSalesTerms"
                labelI18nKey="purchase:payment.acceptSpecificSalesTerms"
                labelClassName="flex flex-wrap"
                link={{
                  url: `${routes.termsOfSales.path}`,
                  target: '_blank',
                  className: 'underline underline-offset-4',
                  i18nValues: { appName: appService.brand() }
                }}
                rules={{ required: true }}
              />
              <button
                type="submit"
                className="btn my-4 max-lg:w-full lg:ml-auto"
                disabled={!formState.isValid || createPaymentLoading}
              >
                {createPaymentLoading && <Loader loaderClass="w-4 h-4 text-white shrink-0 mr-2" />}
                {t('buttons.pay')}
              </button>
            </form>
          </FormProvider>
        </>
      )}
    </div>
  );
};

export default TravelBalancePaymentMethod;
